"use client";

import Button from "@/components/ui/button/Button";
import InlineCreateModal from "@/components/ui/InlineCreateModal";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import TextArea from "@/components/form/input/TextArea";
import { handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import {
  formatInr,
  formatRoundOffInr,
  roundMoney,
  roundOffToWholeRupee,
  roundToDecimals,
} from "@/lib/utils";
import { gstSchema } from "@/lib/validation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

const num = z.preprocess(
  (v) => (v === "" || v === undefined || v === null ? NaN : Number(v)),
  z.number()
);

/** Nested react-hook-form error tree → first message string */
function firstFormErrorMessage(errs) {
  if (!errs || typeof errs !== "object") return null;
  if (typeof errs.message === "string" && errs.message) return errs.message;
  if (Array.isArray(errs)) {
    for (const item of errs) {
      const m = firstFormErrorMessage(item);
      if (m) return m;
    }
    return null;
  }
  for (const v of Object.values(errs)) {
    const m = firstFormErrorMessage(v);
    if (m) return m;
  }
  return null;
}

const schema = z
  .object({
    date: z.string().min(1),
    party_id: z.coerce.number().positive({ message: "Party required" }),
    purchase_kind: z.enum(["REGULAR", "CONCRETE"]),
    tax_type: z.enum(["GST", "NON_GST"]),
    gst_rate: z.string().optional(),
    purchase_invoice_no: z.string().optional(),
    vehicle_no: z.string().min(1, "Vehicle required"),
    remark: z.string().optional(),
    kata_gross_weight: z.string().optional(),
    kata_tare_weight: z.string().optional(),
    net_weight: z.string().optional(),
    royalty_no: z.string().optional(),
    items: z
      .array(
        z.object({
          material_id: z.coerce.number().positive({ message: "Material required" }),
          concrete_quarry_id: z.preprocess((val) => {
            if (val === "" || val === undefined || val === null) return undefined;
            const n = Number(val);
            return Number.isFinite(n) && n > 0 ? n : undefined;
          }, z.number().positive().optional()),
          qty: num.pipe(z.number().positive("Qty required")),
          rate: num.pipe(z.number().positive()),
          remark: z.string().optional(),
        })
      )
      .min(1, "Add at least one item"),
  })
  .superRefine((data, ctx) => {
    if (data.purchase_kind !== "CONCRETE") return;
    if (!String(data.royalty_no ?? "").trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Royalty no. required",
        path: ["royalty_no"],
      });
    }
    (data.items || []).forEach((it, i) => {
      const q = Number(it.concrete_quarry_id);
      if (!Number.isFinite(q) || q <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Quarry required",
          path: ["items", i, "concrete_quarry_id"],
        });
      }
    });
  });

function buildChallanApiBody(data) {
  const shouldApplyGst = data.tax_type === "GST";
  const gross = Number(data.kata_gross_weight);
  const tare = Number(data.kata_tare_weight);
  const manualNet = Number(data.net_weight);
  /** Must not read `body` inside the `body` literal — still in TDZ and throws before any API call. */
  const purchaseKind = data.purchase_kind || "REGULAR";

  const body = {
    date: data.date,
    party_id: Number(data.party_id),
    tax_type: data.tax_type,
    purchase_kind: purchaseKind,
    ...(shouldApplyGst ? { gst_rate: String(data.gst_rate || "").trim() || "18.00" } : {}),
    vehicle_no: data.vehicle_no.trim(),
    remark: (data.remark || "").trim(),
    items: (data.items || []).map((it) => {
      const qty = Number(it.qty);
      const line = {
        material_id: Number(it.material_id),
        rate: roundMoney(Number(it.rate)).toFixed(2),
        qty: roundToDecimals(qty, 3).toFixed(3),
      };
      if (String(it.remark || "").trim()) line.remark = String(it.remark).trim();
      /* Backend expects quarry FK as quarry_id on lines (same as concrete quotations), not concrete_quarry_id. */
      if (purchaseKind === "CONCRETE" && it.concrete_quarry_id != null) {
        line.quarry_id = Number(it.concrete_quarry_id);
      }
      return line;
    }),
  };

  const pinv = String(data.purchase_invoice_no || "").trim();
  if (pinv) body.purchase_invoice_no = pinv;

  if (body.purchase_kind === "CONCRETE") {
    body.royalty_no = String(data.royalty_no || "").trim();
    const firstQ = data.items?.[0]?.concrete_quarry_id;
    if (firstQ != null && Number.isFinite(Number(firstQ)) && Number(firstQ) > 0) {
      body.concrete_quarry_id = Number(firstQ);
    }
  }

  if (Number.isFinite(gross) && Number.isFinite(tare)) {
    body.kata_details = {
      gross_weight: roundToDecimals(gross, 3).toFixed(3),
      tare_weight: roundToDecimals(tare, 3).toFixed(3),
    };
  }

  if (Number.isFinite(manualNet) && manualNet >= 0) {
    body.net_weight = roundToDecimals(manualNet, 3).toFixed(3);
  }

  return body;
}

function getNewPurchaseChallanDefaults(fixedPurchaseKind, challanIdProp) {
  return {
    party_id: undefined,
    date: new Date().toISOString().slice(0, 10),
    purchase_kind:
      !challanIdProp && (fixedPurchaseKind === "REGULAR" || fixedPurchaseKind === "CONCRETE")
        ? fixedPurchaseKind
        : "REGULAR",
    tax_type: "GST",
    gst_rate: "18.00",
    purchase_invoice_no: "",
    vehicle_no: "",
    remark: "",
    kata_gross_weight: "",
    kata_tare_weight: "",
    net_weight: "",
    royalty_no: "",
    items: [{ material_id: undefined, concrete_quarry_id: undefined, qty: "", rate: "", remark: "" }],
  };
}

/** Parse rate from concrete-quotation-rate API (shape may vary). */
function pickQuotationRateFromResponse(payload) {
  if (payload == null) return null;
  if (typeof payload === "number" && Number.isFinite(payload)) return roundMoney(payload);
  if (typeof payload === "object") {
    const nested =
      payload.data != null && typeof payload.data === "object"
        ? pickQuotationRateFromResponse(payload.data)
        : null;
    if (nested != null) return nested;
    const raw = payload.rate ?? payload.quotation_rate ?? payload.price;
    if (raw == null || raw === "") return null;
    const n = roundMoney(Number(raw));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export default function PurchaseChallanForm({ challanId: challanIdProp, fixedPurchaseKind } = {}) {
  const challanId =
    typeof challanIdProp === "number" && Number.isFinite(challanIdProp) && challanIdProp > 0
      ? challanIdProp
      : null;
  const router = useRouter();
  const qc = useQueryClient();
  const [partyModal, setPartyModal] = useState(false);
  const [newParty, setNewParty] = useState({
    name: "",
    gst_number: "",
    address: "",
    contact_no: "",
  });

  const { data: challanNumberData } = useQuery({
    queryKey: ["nextPurchaseChallanNumber"],
    queryFn: async () => (await rmcClient.getPurchaseChallanNumber()).data,
    enabled: !challanId,
  });

  const { data: existingChallan, isLoading: isLoadingChallan } = useQuery({
    queryKey: ["purchaseChallan", challanId],
    queryFn: async () => (await rmcClient.getPurchaseChallan(challanId)).data,
    enabled: !!challanId,
  });

  const { data: parties = [] } = useQuery({
    queryKey: ["purchaseParties", challanId ? "all" : "active"],
    queryFn: async () =>
      (await rmcClient.listPurchaseParties(challanId ? {} : { is_active: true })).data,
  });
  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: getNewPurchaseChallanDefaults(fixedPurchaseKind, challanIdProp),
  });
  const { fields, append, remove } = useFieldArray({ control, name: "items" });
  const selectedPartyId = watch("party_id");
  const taxType = watch("tax_type");
  const itemsWatch = watch("items") || [];
  const kataGrossW = watch("kata_gross_weight");
  const kataTareW = watch("kata_tare_weight");
  const netWeightWatch = watch("net_weight");
  const purchaseKind = watch("purchase_kind");

  const fetchConcreteQuotationRateForLine = React.useCallback(
    async (lineIndex, partyId, materialId, quarryId) => {
      const pid = Number(partyId);
      const mid = Number(materialId);
      const qid = Number(quarryId);
      if (!Number.isFinite(pid) || pid <= 0 || !Number.isFinite(mid) || mid <= 0 || !Number.isFinite(qid) || qid <= 0) {
        return;
      }
      try {
        const { data } = await rmcClient.getConcreteQuotationRate({
          party_id: pid,
          material_id: mid,
          quarry_id: qid,
        });
        const r = pickQuotationRateFromResponse(data);
        if (r != null && Number.isFinite(r)) {
          setValue(`items.${lineIndex}.rate`, String(r), {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      } catch (err) {
        // 404 / no rate: stay silent. Other errors: surface so user knows the lookup failed.
        const status = err?.response?.status;
        if (status != null && status !== 404) {
          toast.error(handleApiError(err));
        }
      }
    },
    [setValue]
  );

  const quarryQueries = useQueries({
    queries: itemsWatch.map((it) => ({
      queryKey: ["partyMaterialQuarries", selectedPartyId, it?.material_id, purchaseKind],
      queryFn: async () =>
        (
          await rmcClient.listPartyMaterialQuarries({
            party_id: selectedPartyId,
            material_id: it.material_id,
          })
        ).data,
      enabled: Boolean(selectedPartyId && it?.material_id && purchaseKind === "CONCRETE"),
    })),
  });

  const { data: materials = [] } = useQuery({
    queryKey: ["materials", selectedPartyId || null],
    queryFn: async () => {
      if (!selectedPartyId) return [];
      return (await rmcClient.listMaterials({ party_id: selectedPartyId })).data;
    },
    enabled: Boolean(selectedPartyId),
  });
  React.useEffect(() => {
    if (!selectedPartyId) {
      setValue(
        "items",
        [{ material_id: undefined, concrete_quarry_id: undefined, qty: "", rate: "", remark: "" }],
        { shouldDirty: true }
      );
      return;
    }
  }, [setValue, selectedPartyId]);

  React.useEffect(() => {
    if (purchaseKind === "CONCRETE") return;
    const items = getValues("items") || [];
    items.forEach((_, idx) => {
      setValue(`items.${idx}.concrete_quarry_id`, undefined, { shouldValidate: false });
    });
    setValue("royalty_no", "", { shouldValidate: false });
  }, [purchaseKind, setValue, getValues]);

  React.useEffect(() => {
    if (!challanId || !existingChallan) return;
    const c = existingChallan;
    const kd = c.kata_details && typeof c.kata_details === "object" ? c.kata_details : null;
    const fallbackQuarry = c.concrete_quarry_id ?? c.concrete_quarry?.id;
    const normalizedItems = Array.isArray(c.items) && c.items.length
      ? c.items.map((it) => ({
          material_id: it?.material_id ?? it?.material?.id,
          concrete_quarry_id:
            it.concrete_quarry_id ?? it.quarry_id ?? it.quarry?.id ?? fallbackQuarry ?? undefined,
          qty: it?.qty != null ? String(it.qty) : "",
          rate: it?.rate != null ? String(it.rate) : "",
          remark: it?.remark || "",
        }))
      : [
          {
            material_id: undefined,
            concrete_quarry_id: undefined,
            qty: "",
            rate: "",
            remark: "",
          },
        ];
    reset({
      date: typeof c.date === "string" ? c.date.slice(0, 10) : "",
      party_id: c.party_id ?? c.party?.id,
      tax_type: c.tax_type || "GST",
      gst_rate: c.gst_rate != null ? String(c.gst_rate) : "18.00",
      purchase_invoice_no: c.purchase_invoice_no != null ? String(c.purchase_invoice_no) : "",
      vehicle_no: c.vehicle_no || "",
      remark: c.remark || "",
      kata_gross_weight:
        kd?.gross_weight != null
          ? String(kd.gross_weight)
          : c.gross_weight != null
            ? String(c.gross_weight)
            : "",
      kata_tare_weight:
        kd?.tare_weight != null
          ? String(kd.tare_weight)
          : c.tare_weight != null
            ? String(c.tare_weight)
            : "",
      net_weight: c.net_weight != null ? String(c.net_weight) : "",
      purchase_kind: c.purchase_kind === "CONCRETE" ? "CONCRETE" : "REGULAR",
      royalty_no: c.royalty_no != null ? String(c.royalty_no) : "",
      items: normalizedItems,
    });
  }, [challanId, existingChallan, reset]);

  const subTotal = roundMoney(
    itemsWatch.reduce((sum, it) => {
      const qtyVal = Number(it?.qty);
      const rateVal = Number(it?.rate);
      if (!Number.isFinite(qtyVal) || qtyVal <= 0 || !Number.isFinite(rateVal)) return sum;
      return sum + qtyVal * rateVal;
    }, 0)
  );
  const gstRateNum = Number(watch("gst_rate") || 0);
  const gstAmount =
    taxType === "GST" ? roundMoney((subTotal * gstRateNum) / 100) : 0;
  const baseTotal = roundMoney(subTotal + gstAmount);
  const { roundedTotal, roundOff } = roundOffToWholeRupee(baseTotal);

  const kataGrossNum = Number(kataGrossW);
  const kataTareNum = Number(kataTareW);
  const autoNet =
    Number.isFinite(kataGrossNum) && Number.isFinite(kataTareNum)
      ? roundToDecimals(kataGrossNum - kataTareNum, 3)
      : null;
  const manualNetNum = Number(netWeightWatch);
  const effectiveNet = Number.isFinite(manualNetNum) && netWeightWatch !== "" && String(netWeightWatch).trim() !== ""
    ? roundToDecimals(manualNetNum, 3)
    : autoNet;
  const totalItemQty = itemsWatch.reduce((s, it) => {
    const q = Number(it?.qty);
    return s + (Number.isFinite(q) && q > 0 ? q : 0);
  }, 0);
  const netCompareClass =
    effectiveNet == null || !Number.isFinite(totalItemQty) || totalItemQty <= 0
      ? "text-gray-400"
      : effectiveNet > totalItemQty
        ? "text-emerald-400"
        : effectiveNet < totalItemQty
          ? "text-red-400"
          : "text-gray-300";
  const qtyVariation =
    effectiveNet != null && Number.isFinite(totalItemQty) && totalItemQty > 0
      ? roundToDecimals(effectiveNet - totalItemQty, 3)
      : null;
  const variationLabel =
    qtyVariation == null
      ? null
      : qtyVariation === 0
        ? "0.000"
        : qtyVariation > 0
          ? `+${qtyVariation.toFixed(3)}`
          : `−${Math.abs(qtyVariation).toFixed(3)}`;
  const nextChallanNo = challanNumberData?.next_challan_no || "--";
  const displayChallanNo = challanId
    ? isLoadingChallan
      ? "…"
      : (existingChallan?.challan_no ?? "—")
    : nextChallanNo;

  const saveParty = useMutation({
    mutationFn: (row) => rmcClient.createPurchaseParty(row),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["purchaseParties"] });
      setValue("party_id", res.data.id);
      setPartyModal(false);
      setNewParty({ name: "", gst_number: "", address: "", contact_no: "" });
      toast.success("Party saved");
    },
  });

  const submit = useMutation({
    mutationFn: (data) => {
      const body = buildChallanApiBody(data);
      if (challanId) return rmcClient.updatePurchaseChallan(challanId, body);
      return rmcClient.createPurchaseChallan(body);
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["purchaseChallans"] });
      qc.invalidateQueries({ queryKey: ["materials"] });
      if (!challanId) {
        qc.invalidateQueries({ queryKey: ["nextPurchaseChallanNumber"] });
        toast.success(
          `${res?.data?.challan_no || "Challan"} saved. Stock updated.`
        );
        reset(getNewPurchaseChallanDefaults(fixedPurchaseKind, challanIdProp));
        return;
      }
      qc.invalidateQueries({ queryKey: ["purchaseChallan", challanId] });
      toast.success(`${res?.data?.challan_no || "Challan"} updated.`);
      router.push("/purchase/challans");
    },
    onError: (err) =>
      toast.error(handleApiError(err)),
  });

  const applyTodayDate = (e) => {
    if (e.key?.toLowerCase() !== "d") return;
    e.preventDefault();
    setValue("date", new Date().toISOString().slice(0, 10), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  if (challanId && isLoadingChallan) {
    return (
      <div className="rounded-xl border border-gray-800 bg-gray-950/40 px-6 py-12 text-center text-sm text-gray-400">
        Loading challan…
      </div>
    );
  }

  return (
    <form
      className="w-full space-y-6"
      onSubmit={handleSubmit(
        (data) => submit.mutate(data),
        (formErrors) => {
          const msg =
            firstFormErrorMessage(formErrors) ||
            "Please fill all required fields (party, vehicle, items, qty, rate).";
          toast.error(msg);
        }
      )}
    >
      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Party & Tax Details
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="pch_date">Date</Label>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <Input
                  id="pch_date"
                  type="date"
                  value={field.value || ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  onKeyDown={applyTodayDate}
                  className="mt-1"
                />
              )}
            />
          {errors.date && (
            <p className="mt-1 text-[11px] text-red-400">{errors.date.message}</p>
          )}
        </div>
        <div>
          <Controller
            name="party_id"
            control={control}
            render={({ field }) => (
              <SearchableSelect
                label="Party"
                value={field.value}
                onChange={field.onChange}
                options={parties}
                createHint="Add New Party"
                onPickCreate={() => setPartyModal(true)}
              />
            )}
          />
          {errors.party_id && (
            <p className="mt-1 text-[11px] text-red-400">{errors.party_id.message}</p>
          )}
        </div>
        {!challanId && (fixedPurchaseKind === "REGULAR" || fixedPurchaseKind === "CONCRETE") ? (
          <div className="sm:col-span-2 rounded-lg border border-gray-800 bg-gray-950/50 px-3 py-2.5">
            <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">Purchase type</p>
            <p className="mt-0.5 text-sm text-white">
              {fixedPurchaseKind === "CONCRETE" ? "Concrete purchase" : "Regular purchase"}
            </p>
            <Link
              href="/purchase/challans/new"
              className="mt-2 inline-block text-xs text-emerald-400/90 hover:text-emerald-300 hover:underline"
            >
              Change type
            </Link>
          </div>
        ) : (
          <div>
            <Label>Purchase type</Label>
            <Controller
              name="purchase_kind"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    { id: "REGULAR", name: "Regular purchase" },
                    { id: "CONCRETE", name: "Concrete purchase" },
                  ]}
                />
              )}
            />
          </div>
        )}
        <div>
          <Label>Tax Type</Label>
          <Controller
            name="tax_type"
            control={control}
            render={({ field }) => (
              <SearchableSelect
                value={field.value}
                onChange={field.onChange}
                options={[
                  { id: "GST", name: "GST" },
                  { id: "NON_GST", name: "Non-GST" },
                ]}
              />
            )}
          />
        </div>
        <div>
          <Label htmlFor="gst_rate">GST Rate (%)</Label>
          <Input
            id="gst_rate"
            type="number"
            step="0.01"
            value={watch("gst_rate") || ""}
            onChange={(e) => setValue("gst_rate", e.target.value, { shouldDirty: true, shouldValidate: true })}
            disabled={taxType !== "GST"}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="veh">Vehicle No</Label>
            <Controller
              name="vehicle_no"
              control={control}
              render={({ field }) => (
                <Input
                  id="veh"
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  onBlur={field.onBlur}
                  className="mt-1 uppercase"
                />
              )}
            />
          {errors.vehicle_no && (
            <p className="mt-1 text-[11px] text-red-400">{errors.vehicle_no.message}</p>
          )}
        </div>
        {purchaseKind === "CONCRETE" && (
          <div>
            <Label htmlFor="pch_royalty">Royalty no.</Label>
            <Controller
              name="royalty_no"
              control={control}
              render={({ field }) => (
                <Input
                  id="pch_royalty"
                  value={field.value || ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  className="mt-1"
                />
              )}
            />
            {errors.royalty_no && (
              <p className="mt-1 text-[11px] text-red-400">{errors.royalty_no.message}</p>
            )}
            <p className="mt-2 text-[10px] text-gray-500">
              Quarry is selected per line under Purchase items (with material and rate from concrete quotations).
            </p>
          </div>
        )}
        <div className="sm:col-span-2">
          <Label htmlFor="pch_inv">Purchase invoice no.</Label>
          <Controller
            name="purchase_invoice_no"
            control={control}
            render={({ field }) => (
              <Input
                id="pch_inv"
                value={field.value || ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="Supplier invoice / PIN"
                className="mt-1"
              />
            )}
          />
        </div>
      </div>
          </div>
          <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-4">
            <Label>Remark</Label>
            <TextArea
              rows={2}
              placeholder="Optional"
              value={watch("remark") || ""}
              onChange={(v) => setValue("remark", v, { shouldDirty: true })}
              className="mt-1 text-xs! rounded-md border-gray-800 bg-gray-950! text-white!"
            />
          </div>

          <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-4 space-y-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Purchase Items
            </p>
            {fields.map((field, idx) => {
              const rowQuarries = quarryQueries[idx]?.data ?? [];
              const rowMaterialId = itemsWatch[idx]?.material_id;
              return (
                <div key={field.id} className="rounded-lg border border-gray-800 p-3">
                  <div
                    className={`grid gap-3 ${purchaseKind === "CONCRETE" ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
                  >
                    <Controller
                      name={`items.${idx}.material_id`}
                      control={control}
                      render={({ field: f }) => (
                        <SearchableSelect
                          label="Material"
                          value={f.value}
                          onChange={(v) => {
                            f.onChange(v);
                            if (purchaseKind === "CONCRETE") {
                              setValue(`items.${idx}.concrete_quarry_id`, undefined, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                              setValue(`items.${idx}.rate`, "", {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            } else {
                              const m = materials.find((x) => Number(x.id) === Number(v));
                              if (m?.last_rate != null && m.last_rate !== "") {
                                setValue(`items.${idx}.rate`, String(m.last_rate), { shouldDirty: true });
                              }
                            }
                          }}
                          options={materials}
                          disabled={!selectedPartyId}
                          getSubLabel={(m) =>
                            purchaseKind === "REGULAR" && m.last_rate
                              ? `Last ₹${Number(m.last_rate).toFixed(2)}`
                              : ""
                          }
                        />
                      )}
                    />
                    {purchaseKind === "CONCRETE" && (
                      <Controller
                        name={`items.${idx}.concrete_quarry_id`}
                        control={control}
                        render={({ field: qf }) => (
                          <div>
                            <SearchableSelect
                              label="Quarry"
                              value={qf.value}
                              onChange={(v) => {
                                qf.onChange(v);
                                // Use getValues — itemsWatch can lag one render behind material selection.
                                const pid = getValues("party_id");
                                const mid = getValues(`items.${idx}.material_id`);
                                if (v != null && v !== "" && pid != null && pid !== "" && mid != null && mid !== "") {
                                  void fetchConcreteQuotationRateForLine(idx, pid, mid, v);
                                }
                              }}
                              options={rowQuarries}
                              disabled={!selectedPartyId || !rowMaterialId}
                              getLabel={(q) => q.name || `Quarry #${q.id}`}
                            />
                            {errors.items?.[idx]?.concrete_quarry_id && (
                              <p className="mt-1 text-[11px] text-red-400">
                                {errors.items[idx].concrete_quarry_id.message}
                              </p>
                            )}
                            {selectedPartyId && rowMaterialId && !quarryQueries[idx]?.isLoading && !rowQuarries.length ? (
                              <p className="mt-1 text-[10px] text-amber-500/90">
                                No quarries for this material. Add under Purchase → Concrete quotations.
                              </p>
                            ) : null}
                          </div>
                        )}
                      />
                    )}
                    <div>
                      <Label>Rate</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={itemsWatch[idx]?.rate || ""}
                        onChange={(e) =>
                          setValue(`items.${idx}.rate`, e.target.value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                        className="mt-1"
                      />
                      {purchaseKind === "CONCRETE" && (
                        <p className="mt-1 text-[10px] text-gray-500">
                          Loaded from concrete quotation rate (party, material, quarry) when you pick a quarry.
                        </p>
                      )}
                    </div>
                    <div className={purchaseKind === "CONCRETE" ? "sm:col-span-1" : ""}>
                      <Label>Qty</Label>
                      <Input
                        type="number"
                        step="0.001"
                        value={itemsWatch[idx]?.qty || ""}
                        onChange={(e) =>
                          setValue(`items.${idx}.qty`, e.target.value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                        className="mt-1"
                      />
                      {errors.items?.[idx]?.qty && (
                        <p className="mt-1 text-[11px] text-red-400">{errors.items[idx].qty.message}</p>
                      )}
                    </div>
                    <div className={purchaseKind === "CONCRETE" ? "sm:col-span-2" : "sm:col-span-2"}>
                      <Label>Remark</Label>
                      <Input
                        value={itemsWatch[idx]?.remark || ""}
                        onChange={(e) => setValue(`items.${idx}.remark`, e.target.value, { shouldDirty: true })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => remove(idx)}
                      disabled={fields.length <= 1}
                    >
                      Remove Item
                    </Button>
                  </div>
                </div>
              );
            })}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                append({
                  material_id: undefined,
                  concrete_quarry_id: undefined,
                  qty: "",
                  rate: "",
                  remark: "",
                })
              }
            >
              Add Item
            </Button>
            {errors.items?.message && <p className="text-[11px] text-red-400">{errors.items.message}</p>}
          </div>

          <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-4 space-y-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              Kata details
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="kata_gross">Gross weight</Label>
                <Controller
                  name="kata_gross_weight"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="kata_gross"
                      type="number"
                      step="0.001"
                      value={field.value || ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      className="mt-1"
                    />
                  )}
                />
              </div>
              <div>
                <Label htmlFor="kata_tare">Tare weight</Label>
                <Controller
                  name="kata_tare_weight"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="kata_tare"
                      type="number"
                      step="0.001"
                      value={field.value || ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      className="mt-1"
                    />
                  )}
                />
              </div>
              <div>
                <Label>Auto net (gross − tare)</Label>
                <Input
                  readOnly
                  value={autoNet != null ? autoNet.toFixed(3) : ""}
                  placeholder="—"
                  className="mt-1 bg-gray-950/80"
                />
              </div>
              <div>
                <Label htmlFor="net_weight">Net weight (optional)</Label>
                <Controller
                  name="net_weight"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="net_weight"
                      type="number"
                      step="0.001"
                      value={field.value || ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="Override for API if needed"
                      className="mt-1"
                    />
                  )}
                />
              </div>
            </div>
            <p className={`text-sm ${netCompareClass}`}>
              {effectiveNet != null && Number.isFinite(totalItemQty) && totalItemQty > 0 ? (
                <>
                  Net weight <span className="font-mono font-semibold">{effectiveNet.toFixed(3)}</span>
                  {" vs "}
                  total Qty <span className="font-mono font-semibold">{totalItemQty.toFixed(3)}</span>
                  {" — "}
                  {variationLabel != null && (
                    <>
                      variation (net − qty){" "}
                      <span className="font-mono font-semibold">{variationLabel}</span>
                      {" — "}
                    </>
                  )}
                  {effectiveNet > totalItemQty
                    ? "Above total qty"
                    : effectiveNet < totalItemQty
                      ? "Below total qty"
                      : "Matches total qty"}
                </>
              ) : (
                <span className="text-gray-500">
                  Enter kata gross/tare and item quantities to compare net weight with billed qty.
                </span>
              )}
            </p>
          </div>
        </div>

        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <div className="rounded-xl border border-gray-800 bg-gray-950/60 px-4 py-3">
            <p className="text-[11px] uppercase tracking-wider text-gray-500">Challan No.</p>
            <Input value={displayChallanNo} disabled className="mt-2 font-semibold tracking-wide" />
          </div>
          <div className="rounded-xl border border-gray-800 bg-gray-900/50 px-4 py-4">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Calculation</p>
            <p className="mt-3 text-sm text-gray-300">
              Sub Total:{" "}
              <span className="font-semibold text-white">₹ {formatInr(subTotal)}</span>
            </p>
            <p className="mt-1 text-sm text-gray-300">
              GST: <span className="font-semibold text-white">₹ {formatInr(gstAmount)}</span>
            </p>
            <p className="mt-1 text-sm text-gray-300">
              Round off:{" "}
              <span className="font-semibold text-white">₹ {formatRoundOffInr(roundOff)}</span>
            </p>
            <p className="mt-1 text-sm text-gray-300">
              Total:{" "}
              <span className="font-semibold text-white">₹ {formatInr(roundedTotal)}</span>
            </p>
          </div>
          <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-4">
            <Button
              type="submit"
              size="md"
              className="w-full"
              disabled={submit.isPending || (challanId && isLoadingChallan)}
            >
              {submit.isPending
                ? challanId
                  ? "Updating…"
                  : "Saving…"
                : challanId
                  ? "Update Challan"
                  : "Save Challan"}
            </Button>
          </div>
        </aside>
      </div>

      <InlineCreateModal
        title="New purchase party"
        isOpen={partyModal}
        onClose={() => setPartyModal(false)}
      >
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input
              value={newParty.name}
              onChange={(e) => setNewParty({ ...newParty, name: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>GST Number</Label>
            <Input
              value={newParty.gst_number}
              onChange={(e) =>
                setNewParty({ ...newParty, gst_number: e.target.value.toUpperCase() })
              }
              className="mt-1"
            />
          </div>
          <div>
            <Label>Contact No</Label>
            <Input
              value={newParty.contact_no}
              onChange={(e) => setNewParty({ ...newParty, contact_no: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Address</Label>
            <Input
              value={newParty.address}
              onChange={(e) => setNewParty({ ...newParty, address: e.target.value })}
              className="mt-1"
            />
          </div>
          <p className="text-[10px] text-gray-500">
            Must match GST format — validated on save.
          </p>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              const p = gstSchema.safeParse(newParty.gst_number);
              if (!p.success) {
                toast.error(p.error.errors[0]?.message || "Invalid GST");
                return;
              }
              saveParty.mutate(newParty);
            }}
          >
            Save party
          </Button>
        </div>
      </InlineCreateModal>
    </form>
  );
}
